











import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { isEmpty, isUndefined } from 'lodash/fp'
import AppForm from '@core/components/AppForm.vue'
import { FormStructure } from '@master/model/structure/FormStructure'
import PersonalDetails from '@company-common/model/kyc-form/PersonalDetails'
import AppClientForm from '@core/components/AppClientForm.vue'
import KYCForm from '@company-common/model/kyc-form/KYCForm'
import { namespace } from 'vuex-class'
import { CountryOptions, NationalityOptions } from '@master/model/options/NationalityAndCountryOptions'
import { EmploymentStatusOptions, PreviousOccupation } from '@master/model/options/EmploymentStatusOptions'

const kycFormStore = namespace('KYCFormStore')

@Component({
  components: {
    AppForm,
    AppClientForm
  }
})
export default class ThePersonalDetails extends Vue {
  @Prop() model!: PersonalDetails
  @Prop() kycForm!: KYCForm
  @Prop() clientIndex!: 0 | 1

  @kycFormStore.State
  private computedValidationResult

  get currentSectionValidationErrors () {
    if (isEmpty(this.computedValidationResult)) {
      return
    }
    const client = this.clientIndex === 1 ? 'Client 2' : 'Client 1'
    const errors = this.computedValidationResult?.['Personal Details']?.[client]
    const finalErrors: { key: string; msg: string }[] = []
    if (!errors) return finalErrors
    errors.map(({ key, message }) => {
      key.map(variableName => {
        finalErrors.push({
          key: variableName,
          msg: message
        })
      })
    })
    return finalErrors
  }

  isLockedField (key) {
    const index = this.clientIndex + 1 // because in kycform, it is client1, client2

    if (isUndefined(this.kycForm['client' + index].myInfo.lockedFields)) {
      return
    }
    return this.kycForm['client' + index].myInfo.keyIsLockedField(key)
  }

  get client () {
    return this.model
  }

  get personalDetailsStructure1 (): FormStructure<PersonalDetails> {
    return [
      {
        label: 'Title',
        key: 'title',
        options: [
          { label: 'Dr', value: 'dr' },
          { label: 'Mr', value: 'mr' },
          { label: 'Mrs', value: 'mrs' },
          { label: 'Mdm', value: 'mdm' },
          { label: 'Miss', value: 'miss' }
        ]
      }, {
        label: 'Name (as per NRIC/FIN/Passport No.)',
        key: 'name',
        disabled: this.isLockedField('name'),
        isMyInfo: this.isLockedField('name')
      }, {
        label: 'NRIC/FIN/Passport No.',
        key: 'nric',
        disabled: this.isLockedField('nric'),
        isMyInfo: this.isLockedField('nric')
      }, {
        label: 'Date of Birth (dd/mm/yyyy)',
        key: 'dob',
        type: 'date',
        disabledFutureDate: true,
        disabled: this.isLockedField('dob'),
        isMyInfo: this.isLockedField('dob'),
        onChange: () => {
          this.updateTrustedIndividual()
          this.resetIntendedRetirementAge() // reset to calculate the retirement age
        }
      }, {
        label: 'Gender',
        key: 'gender',
        options: [
          { label: '', value: '' },
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' }
        ],
        disabled: this.isLockedField('gender'),
        isMyInfo: this.isLockedField('gender'),
        onChange: () => {
          this.resetIntendedRetirementAge() // reset to calculate the retirement age
        }
      }, {
        label: 'Citizenship',
        key: 'citizenship',
        options: [
          { label: '', value: '' },
          { label: 'Singaporean', value: 'singaporean' },
          { label: 'Singapore PR', value: 'pr' },
          { label: 'Singapore Pass Holder', value: 'passholders' },
          { label: 'Other Citizenship', value: 'others' }
        ],
        disabled: this.isLockedField('citizenship'),
        isMyInfo: this.isLockedField('citizenship'),
        tooltip: {
          content: 'Please indicate nationality if "Singapore Pass Holder", "Singapore PR" or "Other Citizenship" is ticked.'
        }
      }, {
        label: 'Please specify Nationality',
        key: 'citizenshipSpecified',
        options: this.nationalityOptions,
        show: this.showSpecifiedCitizenship,
        disabled: this.isLockedField('citizenshipSpecified'),
        isMyInfo: this.isLockedField('citizenshipSpecified')
      }, {
        label: 'Contact Home',
        key: 'contactHome',
        disabled: this.isLockedField('contactHome'),
        isMyInfo: this.isLockedField('contactHome')
      }, {
        label: 'Contact Mobile',
        key: 'contactMobile',
        disabled: this.isLockedField('contactMobile'),
        isMyInfo: this.isLockedField('contactMobile')
      }, {
        label: 'Email',
        key: 'contactEmail',
        classes: ['colspan'],
        disabled: this.isLockedField('contactEmail'),
        isMyInfo: this.isLockedField('contactEmail')
      }, {
        label: 'Residential Address',
        key: 'residentialAddress',
        classes: ['colspan'],
        disabled: this.isLockedField('residentialAddress'),
        isMyInfo: this.isLockedField('residentialAddress')
      }, {
        label: 'Postal Code',
        key: 'postalCode',
        disabled: this.isLockedField('postalCode'),
        isMyInfo: this.isLockedField('postalCode')
      }, {
        label: 'Residential Country',
        key: 'residentialCountry',
        options: this.countryOptions
      }, {
        label: 'Marital Status',
        key: 'maritalStatus',
        options: [
          { label: '', value: '' },
          { label: 'Single', value: 'single' },
          { label: 'Married', value: 'married' },
          { label: 'Divorced', value: 'divorced' },
          { label: 'Widowed', value: 'widowed' }
        ],
        disabled: this.isLockedField('maritalStatus'),
        isMyInfo: this.isLockedField('maritalStatus')
      }, {
        label: 'Smoker',
        key: 'smoker',
        options: [
          { label: '', value: '' },
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' }
        ],
        disabled: this.isLockedField('smoker'),
        isMyInfo: this.isLockedField('smoker')
      }
    ]
  }

  get personalDetailsStructure2 (): FormStructure<PersonalDetails> {
    return [
      {
        label: 'Do you or any family members have any medical condition that requires regular attention from clinic/hospital',
        key: 'familyMedical',
        options: [
          { label: '', value: '' },
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' }
        ]
      },
      {
        label: 'Please Specify Medical Condition',
        key: 'familyMedicalSpecified',
        classes: ['colspan'],
        show: this.model.familyMedical === 'yes'
      },
      {
        label: 'Employment Status',
        key: 'employmentStatus',
        options: EmploymentStatusOptions,
        tooltip: {
          content: 'For Homemakers, Retirees or Students, please indicate accordingly under “Please Specify”. <br/>Where applicable, please provide details of previous/ last employment.'
        }
      }, {
        label: 'Please specify',
        key: 'previousOccupation',
        show: this.showSpecifyPreviousOccupation,
        options: PreviousOccupation
      }, {
        label: 'Intended Retirement Age',
        key: 'customRetirementAge',
        type: 'number'
      }, {
        label: 'Occupation / Designation',
        key: 'occupation',
        disabled: this.isLockedField('occupation'),
        isMyInfo: this.isLockedField('occupation')
      }, {
        label: 'Exact Duties / Function',
        key: 'exactDuties'
      }, {
        label: 'Company Name',
        key: 'companyName',
        classes: ['colspan'],
        disabled: this.isLockedField('companyName'),
        isMyInfo: this.isLockedField('companyName')
      },
      {
        label: 'Are you involved in any of the activities listed below?',
        key: 'involvedInActivities',
        options: [
          { label: '', value: '' },
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' }
        ],
        tooltip: {
          placement: 'right',
          content: '<div style="text-align: justify; width: 360px">Money changer, remittance agent, casino, pawnbroker, moneylender, senior management or equivalent of' +
            'non-profit organization, precious stones & metal dealer, virtual currency (eg.bitcoins, precious metal' +
            'backed digital currency etc), antique trader, owner of cash-intensive (eg. hawker, convenience store,' +
            'second-hand dealer, coffeeshop operator etc), activities related to production / distribution of arms' +
            'related products, trading of oil and natural gas (eg. crude oil, processed oil), agriculture (eg. wheat,' +
            'barley, livestock, fruit, cotton), mineral resource trader (eg. phosphate mines, salt mines, cement plants,' +
            'sulphuric manufacturer). </div>'
        }
      }, {
        label: 'Please Specify Activities',
        key: 'activitiesSpecified',
        classes: ['colspan'],
        show: this.showSpecifyActivities
      }, {
        label: 'Annual Income (S$)',
        key: 'annualIncome',
        options: [
          { label: '', value: '' },
          { label: '< 30,000', value: 'tier1' },
          { label: '30,000 - 50,000', value: 'tier2' },
          { label: '50,001 - 100,000', value: 'tier3' },
          { label: '100,001 - 150,000', value: 'tier4' },
          { label: '150,001 - 300,000', value: 'tier5' },
          { label: '> 300,000', value: 'tier6' }
        ],
        disabled: this.isLockedField('annualIncome'),
        isMyInfo: this.isLockedField('annualIncome')
      }, {
        label: 'Highest Education Level Completed',
        key: 'educationLevel',
        options: [
          { label: '', value: '' },
          { label: 'Primary & below', value: 'primaryandbelow' },
          { label: 'Secondary', value: 'secondary' },
          { label: 'GCE \'O/N\' Level', value: 'gceonlevel' },
          { label: 'Pre-U/JC', value: 'preujc' },
          { label: 'Diploma', value: 'diploma' },
          { label: 'Degree', value: 'degree' },
          { label: 'Post Graduate', value: 'postgrad' }
        ],
        disabled: this.isLockedField('educationLevel'),
        isMyInfo: this.isLockedField('educationLevel'),
        onChange: () => {
          this.updateTrustedIndividual()
        }
      }, {
        label: 'Spoken Language<br/>(Tick all that apply)',
        key: 'spokenLanguage',
        classes: ['colspan'],
        type: 'checkbox',
        compact: true,
        options: [
          { label: 'English', value: 'english' },
          { label: 'Mandarin', value: 'mandarin' },
          { label: 'Malay', value: 'malay' },
          { label: 'Tamil', value: 'tamil' },
          { label: 'Others', value: 'others' }
        ],
        onChange: () => {
          this.updateTrustedIndividual()
        }
      }, {
        label: 'Specify Spoken Language',
        key: 'spokenLanguageSpecified',
        classes: ['colspan'],
        show: this.showSpokenLanguageSpecified
      }, {
        label: 'Written Language<br/>(Tick all that apply)',
        key: 'writtenLanguage',
        classes: ['colspan'],
        type: 'checkbox',
        compact: true,
        options: [
          { label: 'English', value: 'english' },
          { label: 'Chinese', value: 'chinese' },
          { label: 'Malay', value: 'malay' },
          { label: 'Tamil', value: 'tamil' },
          { label: 'Others', value: 'others' }
        ],
        onChange: () => {
          this.updateTrustedIndividual()
        }
      }, {
        label: 'Specify Written Language',
        key: 'writtenLanguageSpecified',
        classes: ['colspan'],
        show: this.showWrittenLanguageSpecified
      }
    ]
  }

  get nationalityOptions () {
    return NationalityOptions
  }

  get countryOptions () {
    return CountryOptions
  }

  showSpecifiedCitizenship () {
    const citizenship = this.model.citizenship
    if (citizenship === 'others' || citizenship === 'pr' || citizenship === 'passholders') {
      return true
    }
    return false
  }

  showSpecifyPreviousOccupation () {
    const employmentStatus = this.model.employmentStatus
    if (employmentStatus === 'unemployed') {
      return true
    }
    return false
  }

  showSpecifyActivities () {
    const involvedInActivities = this.model.involvedInActivities
    if (involvedInActivities === 'yes') {
      return true
    }
    return false
  }

  showSpokenLanguageSpecified () {
    if (this.model.spokenLanguage.includes('others')) {
      return true
    }
    return false
  }

  showWrittenLanguageSpecified () {
    if (this.model.writtenLanguage.includes('others')) {
      return true
    }
    return false
  }

  updateTrustedIndividual () {
    this.kycForm.trustedIndividual.updateAccompanied(this.kycForm.client1, this.kycForm.client2)
  }

  resetIntendedRetirementAge () {
    this.model.intendedRetirementAge = undefined
  }

  @Watch('model.citizenship')
  citizenshipOnChange () {
    if (this.model.citizenship === 'singaporean') {
      this.model.citizenshipSpecified = ''
      this.model.citizenshipSpecified2 = ''
    }
  }
}
